import React from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { LoaderCircle, ShieldCheck } from "lucide-react";
import {
  activateLocalAuthBypassSession,
  isLocalAuthBypassEnabled,
  isSupabaseConfigured,
  supabase,
} from "../lib/supabaseClient.js";
import { recordDriverPortalAuthentication, resolveAuthenticatedEntryPath } from "../lib/driverPortal.js";
import useAccess from "../hooks/useAccess.jsx";
import {
  readLoginNavigationRequest,
  resolveAuthenticatedLoginPath,
} from "../utils/loginReturnPath.js";
import { BRAND_NAME } from "./BrandLogo.jsx";
import LandscapeSlideshow from "./LandscapeSlideshow.jsx";
import PublicFeatureBanner from "./PublicFeatureBanner.jsx";
import "./LoginPage.css";

const primaryButtonClassName = "public-login__button public-login__button--primary";

function resolveAuthMessage(error) {
  const code = error && typeof error === "object" && typeof error.code === "string"
    ? error.code.trim().toLowerCase()
    : "";
  const message = error instanceof Error && typeof error.message === "string"
    ? error.message.trim()
    : error && typeof error === "object" && typeof error.message === "string"
      ? error.message.trim()
      : "";

  if (code === "invalid_credentials" || message.toLowerCase().includes("invalid login credentials")) {
    return "Adresse e-mail ou mot de passe invalide.";
  }

  if (message) {
    return message;
  }

  return "La connexion n'a pas pu être lancée.";
}

export default function LoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    access,
    errorMessage: accessErrorMessage,
    isAuthError,
    isReady,
    session,
  } = useAccess();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const displayedErrorMessage = errorMessage
    || (!isAuthError && accessErrorMessage ? accessErrorMessage : "");
  const supabaseConfigured = isSupabaseConfigured();
  const localAuthBypassEnabled = isLocalAuthBypassEnabled();
  const hasRedirectedToAuthenticatedEntryRef = React.useRef(false);
  const hasObservedUnauthenticatedSwitchAccountRef = React.useRef(false);
  const shouldRecordDriverAuthenticationRef = React.useRef(false);
  const loginNavigationRequest = React.useMemo(
    () => readLoginNavigationRequest(location.search),
    [location.search],
  );

  const redirectToAuthenticatedEntry = React.useCallback(async () => {
    if (hasRedirectedToAuthenticatedEntryRef.current) {
      return;
    }

    hasRedirectedToAuthenticatedEntryRef.current = true;
    try {
      const nextPath = await resolveAuthenticatedEntryPath({ accessState: access });
      navigate(resolveAuthenticatedLoginPath(nextPath, loginNavigationRequest.returnTo), { replace: true });
    } catch (error) {
      hasRedirectedToAuthenticatedEntryRef.current = false;
      setErrorMessage(resolveAuthMessage(error));
      setIsSubmitting(false);
    }
  }, [access, loginNavigationRequest.returnTo, navigate]);

  React.useEffect(() => {
    if (typeof document !== "undefined") {
      document.body.classList.remove("page-shell", "results-page");
      document.body.style.background = "";
      document.documentElement.style.background = "";
    }
  }, []);

  React.useEffect(() => {
    if (!loginNavigationRequest.switchAccount || !isReady || session) {
      return;
    }

    hasObservedUnauthenticatedSwitchAccountRef.current = true;
    // Le chargement des préférences peut remonter cette page après la connexion.
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("switchAccount");
    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
      hash: location.hash,
    }, { replace: true });
  }, [isReady, location, loginNavigationRequest.switchAccount, navigate, session]);

  React.useEffect(() => {
    if (!session) {
      hasRedirectedToAuthenticatedEntryRef.current = false;
      return;
    }

    if (!isReady || isAuthError || accessErrorMessage) {
      return;
    }

    if (
      loginNavigationRequest.switchAccount
      && !hasObservedUnauthenticatedSwitchAccountRef.current
    ) {
      return;
    }

    void redirectToAuthenticatedEntry();
  }, [
    accessErrorMessage,
    isAuthError,
    isReady,
    loginNavigationRequest.switchAccount,
    redirectToAuthenticatedEntry,
    session,
  ]);

  React.useEffect(() => {
    if (!isSubmitting || (!isAuthError && !accessErrorMessage)) {
      return;
    }

    setIsSubmitting(false);
  }, [accessErrorMessage, isAuthError, isSubmitting]);

  React.useEffect(() => {
    if (!session || !shouldRecordDriverAuthenticationRef.current) {
      return;
    }

    shouldRecordDriverAuthenticationRef.current = false;
    if (session?.user?.app_metadata?.grain_account_type !== "driver_portal") {
      return;
    }

    void recordDriverPortalAuthentication().catch(() => {});
  }, [session]);

  async function handleSignInSubmit(event) {
    event.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");

    if (!supabase) {
      setIsSubmitting(false);
      setErrorMessage("Supabase n'est pas encore configuré dans ce frontend.");
      return;
    }

    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (error) {
      setErrorMessage(resolveAuthMessage(error));
      setIsSubmitting(false);
      return;
    }

    shouldRecordDriverAuthenticationRef.current = true;
  }

  async function handleLocalBypassContinue() {
    if (!localAuthBypassEnabled || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      await activateLocalAuthBypassSession();
    } catch (error) {
      setErrorMessage(resolveAuthMessage(error));
      setIsSubmitting(false);
    }
  }

  return (
    <div className="public-login">
      <header className="public-login__header">
        <div className="public-login__header-inner">
          <Link className="public-login__button public-login__button--secondary public-login__back" to="/">
            Retour à l&apos;accueil
          </Link>
        </div>
      </header>

      <main className="public-login__main">
        <section className="public-login__card" aria-labelledby="login-title">
          <p className="public-login__eyebrow">
            <ShieldCheck size={18} aria-hidden="true" />
            Accès sécurisé
          </p>
          <h1 id="login-title" className="public-login__title font-display">Connexion à {BRAND_NAME}</h1>
          <p className="public-login__intro">
            Retrouvez votre espace d’optimisation.
          </p>

          {!supabaseConfigured ? (
            <div className="public-login__alert public-login__alert--warning" role="status">
              Supabase n&apos;est pas encore configuré pour ce frontend. Renseignez au minimum VITE_SUPABASE_URL et VITE_SUPABASE_ANON_KEY pour activer la connexion.
            </div>
          ) : null}

          {displayedErrorMessage ? (
            <div id="login-error" className="public-login__alert public-login__alert--error" role="alert">
              {displayedErrorMessage}
            </div>
          ) : null}

          {!localAuthBypassEnabled || supabase ? (
            <form className="public-login__form" onSubmit={handleSignInSubmit} aria-busy={isSubmitting} aria-describedby={displayedErrorMessage ? "login-error" : undefined}>
              <label className="public-login__field">
                <span>Adresse e-mail</span>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  placeholder="vous@entreprise.fr"
                  required
                />
              </label>

              <label className="public-login__field">
                <span>Mot de passe</span>
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  placeholder="Votre mot de passe"
                  required
                />
              </label>

              <button className={primaryButtonClassName} type="submit" disabled={!supabaseConfigured || isSubmitting}>
                {isSubmitting ? <LoaderCircle className="animate-spin" size={16} aria-hidden="true" /> : <ShieldCheck size={16} aria-hidden="true" />}
                {isSubmitting ? "Connexion en cours..." : "Se connecter"}
              </button>
            </form>
          ) : null}

          {localAuthBypassEnabled ? (
            <div className="public-login__local-access">
              <p>Accès de test local disponible sur cette machine.</p>
              <button className="public-login__button public-login__button--secondary" type="button" onClick={handleLocalBypassContinue} disabled={isSubmitting}>
                {isSubmitting ? <LoaderCircle className="animate-spin" size={16} aria-hidden="true" /> : <ShieldCheck size={16} aria-hidden="true" />}
                {isSubmitting ? "Ouverture en cours..." : "Continuer en mode local"}
              </button>
            </div>
          ) : null}

          <p className="public-login__note">Votre session active est reprise automatiquement.</p>
        </section>

        <div className="public-login__landscape" aria-hidden="true">
          <LandscapeSlideshow />
        </div>
      </main>
      <PublicFeatureBanner />
    </div>
  );
}
